import { useState } from 'react';

/**
 * Like `useState`, but the value survives navigation and reloads within the
 * tab by mirroring to `sessionStorage` under `key`. Used for list-page
 * filters so leaving a list (to open a row) and coming back — or refreshing —
 * keeps the filters the user set, instead of resetting to defaults.
 *
 * Only JSON-serializable values. Dayjs ranges are stored by their callers as
 * ISO-string tuples and rehydrated to dayjs at render, not passed here raw.
 *
 * sessionStorage (not localStorage) on purpose: filters are a per-session
 * working context, cleared when the tab closes — not a permanent preference.
 */
export function usePersistedState<T>(key: string, initial: T): [T, (value: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw !== null ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });

  const set = (value: T | ((prev: T) => T)) => {
    setState((prev) => {
      const next = typeof value === 'function' ? (value as (p: T) => T)(prev) : value;
      try {
        sessionStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Storage full / unavailable (private mode): degrade to in-memory
        // state rather than throwing out of a setter.
      }
      return next;
    });
  };

  return [state, set];
}
